import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useStore } from '../lib/store'
import { clock, shortDate, timeAgo } from '../lib/format'
import type { ActivityEntry, ActivityKind } from '../lib/types'
import { Card } from '../components/ui'
import { EmptyState } from '../components/EmptyState'
import {
  IconCheck,
  IconDoc,
  IconEye,
  IconFace,
  IconLock,
  IconSend,
  IconShield,
} from '../components/icons'
import './pages.css'

const KIND_META: Record<ActivityKind, { icon: typeof IconEye; color: string; verb: string }> = {
  view: { icon: IconEye, color: 'var(--c-accent)', verb: 'viewed' },
  unlock: { icon: IconLock, color: 'var(--cred-vault)', verb: 'unlocked' },
  verify_sent: { icon: IconSend, color: 'var(--c-warn)', verb: 'sent a verification request to' },
  verify_completed: { icon: IconCheck, color: 'var(--cred-live)', verb: 'completed verification —' },
  live_vouch_requested: { icon: IconFace, color: 'var(--c-warn)', verb: 'requested a Live Vouch with' },
  live_vouch_confirmed: { icon: IconShield, color: 'var(--cred-live)', verb: 'confirmed a Live Vouch with' },
  live_vouch_device_changed: { icon: IconShield, color: 'var(--c-warn)', verb: 'saw a device change on a Live Vouch for' },
  vouch_given: { icon: IconShield, color: 'var(--c-accent)', verb: 'recorded a vouch given by' },
  vouch_received: { icon: IconShield, color: 'var(--cred-vault)', verb: 'received a trust-chain vouch —' },
  resume_checked: { icon: IconDoc, color: 'var(--c-accent)', verb: 'checked the résumé of' },
}

const FILTERS: { value: ActivityKind | 'all'; label: string }[] = [
  { value: 'all', label: 'All activity' },
  { value: 'live_vouch_confirmed', label: 'Live Vouches' },
  { value: 'verify_sent', label: 'Requests' },
  { value: 'unlock', label: 'Unlocks' },
  { value: 'view', label: 'Views' },
]

function ActivityItem({ e }: { e: ActivityEntry }) {
  const meta = KIND_META[e.kind]
  const Icon = meta.icon
  return (
    <div className="act-item">
      <div className="act-item__icon" style={{ color: meta.color }}>
        <Icon size={17} />
      </div>
      <div className="act-item__body">
        <div className="act-item__line">
          <b>{e.actor}</b> {meta.verb}{' '}
          {e.candidateName &&
            (e.candidateId ? (
              <Link to={`/candidates/${e.candidateId}`} style={{ fontWeight: 700, color: 'var(--c-pale)' }}>
                {e.candidateName}
              </Link>
            ) : (
              <b>{e.candidateName}</b>
            ))}
        </div>
        {e.detail && <div className="act-item__detail">{e.detail}</div>}
      </div>
      <div className="act-item__time" title={`${shortDate(e.at)} ${clock(e.at)}`}>
        {timeAgo(e.at)}
      </div>
    </div>
  )
}

export function Activity() {
  const { activity } = useStore()
  const [filter, setFilter] = useState<ActivityKind | 'all'>('all')

  const filtered = useMemo(() => {
    const list = filter === 'all' ? activity : activity.filter((e) => e.kind === filter)
    return [...list].sort((a, b) => +new Date(b.at) - +new Date(a.at))
  }, [activity, filter])

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Activity</h1>
          <p className="page__sub">Every action your team takes is logged — an audit trail you can stand behind.</p>
        </div>
      </div>

      <div className="pill-row" style={{ marginBottom: '1.25rem' }}>
        {FILTERS.map((f) => (
          <button
            key={f.value}
            className="skill-pill"
            onClick={() => setFilter(f.value)}
            style={{
              cursor: 'pointer',
              borderColor: filter === f.value ? 'var(--c-glass-bdr-hi)' : undefined,
              background: filter === f.value ? 'var(--surface-hover)' : undefined,
              color: filter === f.value ? 'var(--c-text)' : undefined,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Card>
        {filtered.length === 0 ? (
          <EmptyState title="Nothing here yet" body="Actions you and your team take will show up in this log." />
        ) : (
          <div className="activity-list">
            {filtered.map((e) => (
              <ActivityItem key={e.id} e={e} />
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
